// src/Dashboard/pages/teacher/Events/components/EventDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaExternalLinkAlt,
  FaFlag,
  FaImage,
  FaLayerGroup,
  FaMapMarkerAlt,
  FaSave,
  FaTimes,
  FaUpload,
  FaUsers,
} from "react-icons/fa";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import {
  getEmptyEventForm,
  getEventPhotoSrc,
  normalizeEventForForm,
} from "../utils/eventsDashboardUtils";

const quillModules = {
  toolbar: [
    [{ header: [false, 3, 4] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

function formatDateFr(dateValue) {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function CategoryToggleGrid({ options = [], selected = [], onToggle }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {options.map((category) => {
        const active = selected.includes(category);

        return (
          <button
            key={category}
            type="button"
            onClick={() => onToggle(category)}
            className={`rounded-2xl border px-3 py-2 text-left text-xs font-bold transition ${
              active
                ? "border-[#013E8E] bg-[#013E8E] text-white shadow-sm"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            }`}
          >
            {category}
          </button>
        );
      })}
    </div>
  );
}

export default function EventDrawer({
  isOpen,
  mode = "create",
  event,
  shortnameClub,
  typeOptions = [],
  levelOptions = [],
  categoryOptions = [],
  saving,
  uploadingImage,
  onClose,
  onSubmit,
  onUploadImage,
}) {
  const [formData, setFormData] = useState(getEmptyEventForm(shortnameClub));
  const [imgError, setImgError] = useState(false);
  const [imgVersion, setImgVersion] = useState(0);

  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploadProgress, setUploadProgress] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    if (mode === "edit" && event) {
      setFormData(normalizeEventForForm(event));
    } else {
      setFormData(getEmptyEventForm(shortnameClub));
    }

    setImgError(false);
    setSelectedFile(null);
    setOutputFormat("webp");
    setUploadProgress(0);
    setImgVersion((prev) => prev + 1);
  }, [isOpen, mode, event, shortnameClub]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (keyboardEvent) => {
      if (keyboardEvent.key === "Escape" && !saving && !uploadingImage) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, uploadingImage, onClose]);

  const title = mode === "edit" ? "Modifier l’événement" : "Créer un événement";
  const subtitle =
    mode === "edit"
      ? "Mettez à jour les informations, les catégories et l’image de l’événement."
      : "Renseignez les informations principales et sélectionnez les catégories concernées.";

  const canSubmit = useMemo(() => {
    return (
      String(formData.titre || "").trim() &&
      formData.start &&
      formData.type_event &&
      formData.level_event &&
      !saving
    );
  }, [formData, saving]);

  const previewSrc = useMemo(() => {
    if (imgError) return "/img/no-image.jpg";

    const src = getEventPhotoSrc({
      photo_url: formData.photo_url,
    });

    if (!src) return "/img/no-image.jpg";

    return `${src}${src.includes("?") ? "&" : "?"}v=${imgVersion}`;
  }, [formData.photo_url, imgError, imgVersion]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "photo_url") {
      setImgError(false);
      setImgVersion((prev) => prev + 1);
    }
  };

  const toggleCategory = (category) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.cate_event) ? prev.cate_event : [];
      const exists = current.includes(category);

      return {
        ...prev,
        cate_event: exists
          ? current.filter((item) => item !== category)
          : [...current, category],
      };
    });
  };

  const clearCategories = () => {
    setFormData((prev) => ({
      ...prev,
      cate_event: [],
    }));
  };

  const handleSubmit = async (submitEvent) => {
    submitEvent.preventDefault();

    if (!canSubmit) return;

    if (formData.end && new Date(formData.end) < new Date(formData.start)) {
      alert("La date de fin ne peut pas être antérieure à la date de début.");
      return;
    }

    await onSubmit?.(formData);
  };

  const handleUpload = async () => {
    if (mode !== "edit" || !event?.id) {
      alert("Enregistrez d’abord l’événement avant de téléverser une image.");
      return;
    }

    if (!selectedFile) return;

    setUploadProgress(0);

    const photoUrl = await onUploadImage?.({
      eventId: event.id,
      file: selectedFile,
      format: outputFormat,
      onUploadProgress: (progressEvent) => {
        if (!progressEvent.total) return;

        setUploadProgress(
          Math.round((progressEvent.loaded * 100) / progressEvent.total)
        );
      },
    });

    if (photoUrl) {
      setFormData((prev) => ({
        ...prev,
        photo_url: photoUrl,
      }));

      setImgError(false);
      setImgVersion((prev) => prev + 1);
      setSelectedFile(null);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-5xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {mode === "edit" ? "Modification" : "Nouvel événement"}
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              {title}
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              {subtitle}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving || uploadingImage}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5"
        >
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr,340px]">
            <div className="space-y-5">
              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Informations principales
                </p>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Titre *
                    </label>

                    <input
                      type="text"
                      value={formData.titre}
                      onChange={(event) =>
                        updateField("titre", event.target.value)
                      }
                      placeholder="Ex : Tournoi départemental"
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      required
                      autoFocus
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Description
                    </label>

                    <textarea
                      value={formData.description}
                      onChange={(event) =>
                        updateField("description", event.target.value)
                      }
                      rows={4}
                      placeholder="Informations utiles, consignes, documents..."
                      className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Date début *
                      </label>

                      <input
                        type="date"
                        value={formData.start}
                        onChange={(event) =>
                          updateField("start", event.target.value)
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        required
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Date fin
                      </label>

                      <input
                        type="date"
                        value={formData.end}
                        onChange={(event) =>
                          updateField("end", event.target.value)
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Type *
                      </label>

                      <select
                        value={formData.type_event}
                        onChange={(event) =>
                          updateField("type_event", event.target.value)
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        required
                      >
                        <option value="">Sélectionner</option>
                        {typeOptions.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Niveau *
                      </label>

                      <select
                        value={formData.level_event}
                        onChange={(event) =>
                          updateField("level_event", event.target.value)
                        }
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        required
                      >
                        <option value="">Sélectionner</option>
                        {levelOptions.map((level) => (
                          <option key={level} value={level}>
                            {level}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Lieu
                    </label>

                    <input
                      type="text"
                      value={formData.lieu_event}
                      onChange={(event) =>
                        updateField("lieu_event", event.target.value)
                      }
                      placeholder="Salle, ville, adresse..."
                      className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    />
                  </div>
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Catégories concernées
                    </p>

                    <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Cliquez sur une catégorie pour la sélectionner, cliquez à
                      nouveau pour la retirer.
                    </p>
                  </div>

                  {formData.cate_event?.length > 0 ? (
                    <button
                      type="button"
                      onClick={clearCategories}
                      className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                    >
                      Tout désélectionner
                    </button>
                  ) : null}
                </div>

                <div className="mt-4">
                  <CategoryToggleGrid
                    options={categoryOptions}
                    selected={formData.cate_event || []}
                    onToggle={toggleCategory}
                  />
                </div>
              </div>

              <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Horaires & liens
                </p>

                <div className="mt-4 space-y-4">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Horaires formatés
                    </label>

                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                      <ReactQuill
                        value={formData.horaire_event}
                        onChange={(value) =>
                          updateField("horaire_event", value)
                        }
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Ex : Pesée 8h30, début 9h30..."
                        theme="snow"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        URL photo
                      </label>

                      <input
                        type="text"
                        value={formData.photo_url}
                        onChange={(event) =>
                          updateField("photo_url", event.target.value)
                        }
                        placeholder="https://... ou /uploads/..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        URL agenda
                      </label>

                      <input
                        type="url"
                        value={formData.agenda_url}
                        onChange={(event) =>
                          updateField("agenda_url", event.target.value)
                        }
                        placeholder="https://..."
                        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                      <input
                        type="checkbox"
                        checked={!!formData.is_active}
                        onChange={(event) =>
                          updateField("is_active", event.target.checked)
                        }
                        className="h-4 w-4 accent-[#013E8E]"
                      />
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Afficher sur le site
                      </span>
                    </label>

                    <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
                      <input
                        type="checkbox"
                        checked={Number(formData.can_register ?? 1) === 1}
                        onChange={(event) =>
                          updateField("can_register", event.target.checked ? 1 : 0)
                        }
                        className="h-4 w-4 accent-[#013E8E]"
                      />
                      <span className="text-sm font-bold text-slate-700 dark:text-slate-200">
                        Inscriptions ouvertes
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <aside className="space-y-4">
              <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                <div className="relative p-3">
                  <img
                    src={previewSrc}
                    alt={formData.titre || "Aperçu événement"}
                    onError={() => setImgError(true)}
                    className="h-64 w-full rounded-2xl object-contain bg-slate-50 dark:bg-slate-800/60"
                  />

                  <span className="absolute bottom-5 right-5 inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900">
                    <FaImage />
                  </span>
                </div>

                <div className="space-y-2 px-4 pb-4 text-sm text-slate-700 dark:text-slate-200">
                  {formData.type_event ? (
                    <div className="flex items-center gap-2">
                      <FaLayerGroup className="text-slate-400" />
                      <span className="truncate">{formData.type_event}</span>
                    </div>
                  ) : null}

                  {formData.level_event ? (
                    <div className="flex items-center gap-2">
                      <FaFlag className="text-slate-400" />
                      <span className="truncate">{formData.level_event}</span>
                    </div>
                  ) : null}

                  {formData.lieu_event ? (
                    <div className="flex items-center gap-2">
                      <FaMapMarkerAlt className="text-slate-400" />
                      <span className="truncate">{formData.lieu_event}</span>
                    </div>
                  ) : null}

                  {formData.cate_event?.length > 0 ? (
                    <div className="flex items-center gap-2">
                      <FaUsers className="text-slate-400" />
                      <span className="truncate">
                        {formData.cate_event.join(", ")}
                      </span>
                    </div>
                  ) : null}

                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="text-slate-400" />
                    <span className="truncate">
                      {formatDateFr(formData.start)}
                      {formData.end ? ` → ${formatDateFr(formData.end)}` : ""}
                    </span>
                  </div>

                  {formData.agenda_url ? (
                    <a
                      href={formData.agenda_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-xs font-bold text-indigo-700 underline decoration-dotted dark:text-indigo-300"
                    >
                      Ouvrir l’agenda
                      <FaExternalLinkAlt />
                    </a>
                  ) : null}
                </div>
              </div>

              {mode === "edit" ? (
                <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/40">
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Téléverser une image
                  </p>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setSelectedFile(event.target.files?.[0] || null)
                    }
                    className="mt-3 block w-full text-sm text-slate-700 dark:text-slate-200"
                  />

                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      Format
                    </span>

                    <select
                      value={outputFormat}
                      onChange={(event) => setOutputFormat(event.target.value)}
                      disabled={uploadingImage}
                      className="rounded-xl border border-slate-200 bg-white px-2 py-1 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                    >
                      <option value="webp">WEBP</option>
                      <option value="jpg">JPG</option>
                      <option value="png">PNG</option>
                    </select>
                  </div>

                  {uploadProgress > 0 ? (
                    <div className="mt-3">
                      <progress
                        className="h-2 w-full overflow-hidden rounded-full"
                        value={uploadProgress}
                        max="100"
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        {uploadProgress}%
                      </p>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploadingImage}
                    className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-3 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <FaUpload />
                    {uploadingImage ? "Transfert..." : "Téléverser"}
                  </button>

                  <p className="mt-3 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    L’image sera optimisée côté serveur. Pour un nouvel
                    événement, créez-le d’abord, puis ouvrez-le en modification.
                  </p>
                </div>
              ) : (
                <div className="rounded-[24px] border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
                  Vous pourrez téléverser une image optimisée après la création
                  de l’événement.
                </div>
              )}
            </aside>
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={saving || uploadingImage}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Annuler
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <FaSave />
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}